import * as React from 'react'
import {Feature} from '../../models'
import {IFeatureLayerInfo} from '../../interfaces/models'
import {IFieldInfo, IUser} from '../../interfaces/reducers'
import OverlayLoader from '../Loading/OverlayLoading'
import {parseFeatureFields, featureHasAllFields} from '../../helpers'
import {ServiceHub} from '../../ServiceHub'
import {AttachmentsViewer} from '../Controls/Attachments/AttachmentsViewer2'
import {IMapDispatcher} from '../../interfaces/dispatchers'
import Axios from 'axios'

export interface Props {
  feature: Feature
  layerInfo: IFeatureLayerInfo
  style?: React.CSSProperties
  user?: IUser
  mapDispatcher?: IMapDispatcher
}

interface State {
  loaded: boolean
  feature: Feature
  deleted: boolean
  getAttachments: boolean
}

class ReadOnlyView extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      loaded: featureHasAllFields(this.props.feature, this.props.layerInfo),
      feature: props.feature,
      deleted: false,
      getAttachments: true
    }
  }

  componentDidMount() {
    // Sequence: enrich the feature first, then load attachments — otherwise
    // attachAttachments tags off the bare feature properties and the request
    // payload comes back as [].
    this.loadDependencies(this.props).then(() => this.loadAttachmentsOnce(this.props))
  }

  // componentDidUpdate is the single hook for re-loads (loadDependencies +
  // attachments) — defined further down to merge with the existing one that
  // covered loadDependencies only. See line where it lives below.

  protected loadAttachmentsOnce(props: Props) {
    if (!props.layerInfo.sourceDescription.hasAttachments) return
    if (!this.state.getAttachments) return
    // Fire exactly once per mount/feature switch. render() no longer triggers
    // network calls — moving the side-effect out of render also stops the
    // re-fire storm where every setState scheduled another POST.
    ServiceHub.gisCrudService
      .attachAttachments({layerInfo: props.layerInfo, features: [this.state.feature]})
      .then(features => {
        this.setState({...this.state, feature: features[0], getAttachments: false})
      })
      .catch(error => {
        // Don't loop forever on transient failures; mark attachment-load done.
        this.setState({...this.state, getAttachments: false})
        console.error('attachAttachments failed', error)
      })
  }

  protected getDefaultSqlWhere(props: Props): string {
    return `"${props.layerInfo.sourceDescription.idField}" = ${
      props.feature.getProperties()[props.layerInfo.sourceDescription.idField]
    }`
  }

  protected sortFields(fields: Array<JSX.Element>, layerInfo: IFeatureLayerInfo): Array<JSX.Element> {
    const fieldInfos: IFieldInfo[] = []
    for (const fn in layerInfo.fields) {
      fieldInfos.push(layerInfo.fields[fn])
    }
    const fieldNames: string[] = fieldInfos.sort((a, b) => (a.z > b.z ? -1 : 1)).map(fi => fi.fieldName)

    if (fieldNames && fieldNames.length > 0) {
      const sorted: Array<JSX.Element> = fields.sort((a, b) => {
        if (a.key == 'Coordinates') {
          return 1
        }
        if (a.key && b.key) {
          return fieldNames.indexOf((a as any).key) > fieldNames.indexOf((b as any).key) ? 1 : -1
        }
        return 0
      })
      return sorted
    }
    return fields
  }

  protected getAttachmentsViewer(feature: Feature): JSX.Element {
    return (
      <AttachmentsViewer
        readonly={true}
        adds={feature.attachmentChanges.adds}
        updates={feature.attachmentChanges.updates}
        deletes={feature.attachmentChanges.deletes}
        attachments={feature.attachments}
        onAttachmentsChange={changes => {}}
        feature={feature}
        uploadToArchive={false}
      />
    )
  }

  protected loadDependencies(props: Props): Promise<void> {
    const hasAll = featureHasAllFields(props.feature, props.layerInfo)
    if (hasAll) {
      return new Promise(resolve => {
        this.setState({...this.state, loaded: true, feature: props.feature.clone()}, () => resolve())
      })
    }
    return ServiceHub.gisCrudService
      .execute(props.layerInfo, {
        where: this.getDefaultSqlWhere(props),
        outFields: ['*']
      })
      .then(result => {
        if (!result || result.length === 0) {
          // /Query returned nothing — keep the partial feature and stop the
          // loader so the user sees whatever fields were already on it. Don't
          // flip to deleted because the failure mode here is often "feature
          // came in without an Id" rather than a real delete.
          return new Promise<void>(resolve => {
            this.setState({...this.state, loaded: true}, () => resolve())
          })
        }
        const newAtts = result[0].getProperties()
        props.feature.setProperties(newAtts)
        return new Promise<void>(resolve => {
          this.setState({...this.state, loaded: true, feature: props.feature.clone()}, () => resolve())
        })
      })
      .catch(error => {
        return new Promise<void>(resolve => {
          this.setState({...this.state, loaded: true, deleted: true}, () => resolve())
          console.error(error)
        })
      })
  }

  protected async getFiles(feature: Feature) {
    const tags = []
    if (feature.getProperties().EKATTE) {
      tags.push(`екатте:${feature.getProperties().EKATTE}`)
    }
    if (feature.getProperties().ParcelNumber) {
      tags.push(`упи:${feature.getProperties().ParcelNumber}`)
    }
    if (feature.getProperties().QuarterNumber) {
      tags.push(`квартал:${feature.getProperties().QuarterNumber}`)
    }

    const data = JSON.stringify(tags)
    const apiUrl = process.env.HOST_API_URL + '/GetFilesByTags'
    const accessToken = localStorage.getItem('accessToken')
    const accessTokenWithoutQuotes = accessToken && accessToken.replace(/['"]+/g, '')
    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: apiUrl,
      headers: {'Content-Type': 'application/json', Authorization: `Bearer ${accessTokenWithoutQuotes}`},
      data: data
    }

    if (!accessToken) {
      return []
    }

    try {
      Axios.request(config)
        .then(result => {
          return result.data
        })
        .catch(error => {
          console.log(error)
        })
    } catch (e) {
      console.log('ERROR: ', e)
      return []
    }
  }

  componentDidUpdate(prevProps: Props) {
    // The popup may swap the feature/layerInfo (page switch in SimpleMapPopup).
    // Same sequencing as mount: enrich first, then attachments.
    if (prevProps.feature !== this.props.feature || prevProps.layerInfo !== this.props.layerInfo) {
      this.setState({...this.state, getAttachments: true}, () => {
        this.loadDependencies(this.props).then(() => this.loadAttachmentsOnce(this.props))
      })
    }
  }

  protected getLabel(fieldName: string, label: string, feature: Feature, layerInfo: IFeatureLayerInfo): JSX.Element {
    return <div className="detail-view-label">{layerInfo.fields[fieldName].label}</div>
  }

  protected getValue(fieldName: string, value: string, feature: Feature, layerInfo: IFeatureLayerInfo) {
    return <div className="detail-view-value">{value}</div>
  }

  protected getLine(name: string, path: string): JSX.Element {
    return (
      <div>
        <div>
          {name} - {path}
        </div>
      </div>
    )
  }

  protected getRow(
    fieldName: string,
    label: string,
    value: string,
    feature: Feature,
    layerInfo: IFeatureLayerInfo
  ): JSX.Element {
    return (
      <div key={fieldName} style={{display: 'flex', flexDirection: 'column', marginBottom: '15px'}}>
        {this.getLabel(fieldName, label, feature, layerInfo)}
        {this.getValue(fieldName, value, feature, layerInfo)}
      </div>
    )
  }

  protected processField(fieldInfo: IFieldInfo, layerInfo: IFeatureLayerInfo, feature: Feature): boolean {
    return !!fieldInfo.visible
  }

  protected renderFeatures(feature: Feature, layerInfo: IFeatureLayerInfo): Array<JSX.Element> {
    const fields: Array<JSX.Element> = []
    const featureValues = parseFeatureFields(feature, layerInfo)
    for (const fieldName in featureValues) {
      if (this.processField(layerInfo.fields[fieldName], layerInfo, feature) && !!featureValues[fieldName]) {
        fields.push(
          this.getRow(fieldName, layerInfo.fields[fieldName].label, featureValues[fieldName], feature, layerInfo)
        )
      }
    }
    return fields
  }

  protected renderFields(fields: Array<JSX.Element>): JSX.Element {
    // can be used to override the fields container
    return <div>{fields}</div>
  }

  render() {
    if (this.state.deleted) {
      return <div className="single-line-message">Обектът е изтрит</div>
    }
    return (
      <div style={{...this.props.style, position: 'relative', height: '100%'}}>
        <OverlayLoader size="60px" show={!this.state.loaded} />
        {this.renderFields(
          this.sortFields(this.renderFeatures(this.state.feature, this.props.layerInfo), this.props.layerInfo)
        )}
        {this.props.layerInfo.sourceDescription.hasAttachments ? this.getAttachmentsViewer(this.state.feature) : null}
      </div>
    )
  }
}

export default ReadOnlyView
